import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTenant } from './TenantContext';

/**
 * AuthContext
 * ───────────
 * Manages:
 *   - JWT access / refresh token lifecycle
 *   - Current user session
 *   - Login / logout / register flows
 *   - Role-based permission helpers
 */

const AuthContext = createContext(null);

const TOKEN_KEY = 'uhelp_access_token';
const REFRESH_KEY = 'uhelp_refresh_token';

// ─── Provider ─────────────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const { tenantId } = useTenant();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // initial hydration

  // ── Token helpers ──────────────────────────────────────────────────────────
  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    // Attach to Axios instance default headers
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    api.defaults.headers.common['X-Tenant-ID'] = tenantId;
  };

  const clearTokens = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    delete api.defaults.headers.common['Authorization'];
  };

  // ── Hydrate session on mount ───────────────────────────────────────────────
  useEffect(() => {
    const hydrate = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['X-Tenant-ID'] = tenantId;

      try {
        const { data } = await api.get('/api/auth/me');
        if (data.success) setUser(data.data);
      } catch {
        // Try refresh
        const refresh = localStorage.getItem(REFRESH_KEY);
        if (refresh) {
          try {
            const { data } = await api.post('/api/auth/refresh', { refreshToken: refresh });
            if (data.success) {
              saveTokens(data.accessToken, data.refreshToken);
              const me = await api.get('/api/auth/me');
              setUser(me.data.data);
            }
          } catch {
            clearTokens();
          }
        } else {
          clearTokens();
        }
      } finally {
        setLoading(false);
      }
    };
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password }, {
      headers: { 'X-Tenant-ID': tenantId },
    });
    if (data.success) {
      saveTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.firstName}!`);
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // ── Register ───────────────────────────────────────────────────────────────
  const register = useCallback(async (payload) => {
    const { data } = await api.post('/api/auth/register', payload, {
      headers: { 'X-Tenant-ID': tenantId },
    });
    if (data.success) {
      saveTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      toast.success('Account created successfully!');
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch { /* no-op */ }
    clearTokens();
    setUser(null);
    toast.success('Logged out successfully.');
  }, []);

  // ── Update current user ────────────────────────────────────────────────────
  const updateUser = useCallback(async (updates) => {
    const { data } = await api.put('/api/auth/me', updates);
    if (data.success) {
      setUser(data.data);
      toast.success('Profile updated.');
    }
    return data;
  }, []);

  // ── RBAC helpers ───────────────────────────────────────────────────────────
  const ROLE_ORDER = { customer: 0, agent: 1, admin: 2, superadmin: 3 };
  const hasRole = (...roles) => roles.includes(user?.role);
  const hasMinRole = (minRole) => (ROLE_ORDER[user?.role] ?? -1) >= (ROLE_ORDER[minRole] ?? 99);
  const isAuthenticated = !!user;
  const isAgent = hasMinRole('agent');
  const isAdmin = hasMinRole('admin');

  const value = {
    user,
    setUser,
    loading,
    isAuthenticated,
    isAgent,
    isAdmin,
    login,
    register,
    logout,
    updateUser,
    hasRole,
    hasMinRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};

export default AuthContext;
